import { useState, type FormEvent, type JSX } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { AuthLayout } from '@/layouts/AuthLayout';
import { clearSession, startSession } from '@/lib/session';

import styles from './LoginPage.module.scss';
import { platformLogin } from '../api/platform-auth.api';

/** OS-1710 — explica por qué se cerró la sesión anterior (`/login?expired=1` / `?idle=1`). */
function sessionNotice(params: URLSearchParams): string {
  if (params.get('expired') === '1') {
    return 'Tu sesión expiró. Inicia sesión de nuevo.';
  }
  if (params.get('idle') === '1') {
    return 'Cerramos tu sesión por inactividad. Inicia sesión de nuevo.';
  }
  return '';
}

export function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const notice = sessionNotice(searchParams);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // MFA deshabilitado temporalmente (ver `PlatformAuthService.login`): el login con
      // contraseña abre la sesión completa.
      const { accessToken } = await platformLogin(email, password);
      clearSession();
      startSession(accessToken, email);
      void navigate('/dashboard', { replace: true });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthLayout title="Acceso Admin" subtitle="OpsSynk Platform Console">
      <form className={styles.form} onSubmit={(e) => void handleSubmit(e)}>
        {notice && !error && (
          <p className={styles.notice} role="status">
            {notice}
          </p>
        )}
        {error && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}

        <div className={styles.field}>
          <label className={styles.label} htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            className={styles.input}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="admin@opssynk.com"
            required
            autoComplete="username"
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="password">Contraseña</label>
          <input
            id="password"
            type="password"
            className={styles.input}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            required
            autoComplete="current-password"
          />
        </div>

        <button type="submit" className={styles.submitBtn} disabled={loading}>
          {loading ? 'Verificando…' : 'Continuar'}
        </button>
      </form>
    </AuthLayout>
  );
}
